import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useVendorAuth, vendorFetch } from '../../contexts/VendorAuthContext';
import { toast } from 'sonner';
import { Building2, Eye, EyeOff, ArrowLeft, Mail } from 'lucide-react';

export default function VendorLogin() {
  const { login } = useVendorAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [resending, setResending] = useState(false);

  const resendVerification = async () => {
    if (!form.email.trim()) { toast.error('Enter your email address first'); return; }
    setResending(true);
    try {
      await vendorFetch('/partner/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify({ email: form.email.trim().toLowerCase() }),
      });
      toast.success('Verification email resent — check your inbox and spam folder');
      setShowResend(false);
    } catch { toast.error('Could not resend. Try registering again.'); }
    setResending(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email.trim(), form.password);
      nav('/partner/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#3b4872] flex flex-col items-center justify-center px-4" data-testid="vendor-login-page">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-md p-8 space-y-6">
        <div className="flex items-center gap-3">
          <img src="/ellora-logo-blue.png" alt="Ellora EPC" className="h-9 w-auto" />
          <div className="border-l border-slate-200 pl-3">
            <div className="font-bold text-sm text-slate-900">Partner Login</div>
            <div className="text-[10px] text-slate-400">Ellora EPC Vendor Portal</div>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Email</label>
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
              className="w-full border border-slate-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#3b4872]"
              placeholder="you@company.com" required data-testid="vendor-email-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Password</label>
            <div className="relative">
              <input type={showPw ? 'text' : 'password'} value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                className="w-full border border-slate-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#3b4872] pr-10"
                required data-testid="vendor-pw-input" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-2.5 text-slate-400">
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <button type="submit" disabled={loading}
            className="w-full bg-[#3b4872] hover:bg-[#2e3a5c] text-white py-2.5 rounded font-bold text-sm disabled:opacity-50"
            data-testid="vendor-login-btn">
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
        <div className="flex items-center justify-between text-xs text-slate-500">
          <Link to="/partner/forgot-password" className="hover:text-[#3b4872]">Forgot password?</Link>
          <Link to="/partner/register" className="text-[#3b4872] font-semibold hover:underline">Register →</Link>
        </div>
        {/* Resend verification */}
        {!showResend ? (
          <button type="button" onClick={() => setShowResend(true)}
            className="w-full text-xs text-slate-400 hover:text-slate-600 text-center py-1">
            Didn't receive your verification email?
          </button>
        ) : (
          <div className="bg-blue-50 border border-blue-200 rounded p-3 space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold text-blue-800">
              <Mail size={13} /> Resend verification email
            </div>
            <p className="text-xs text-blue-700">Enter your registered email and we'll send a new verification link.</p>
            <div className="flex gap-2">
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="you@company.com"
                className="flex-1 border border-blue-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500"
                data-testid="resend-email-input" />
              <button type="button" onClick={resendVerification} disabled={resending}
                className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-bold disabled:opacity-50"
                data-testid="resend-btn">
                {resending ? '…' : 'Resend'}
              </button>
              <button type="button" onClick={() => setShowResend(false)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
          </div>
        )}
        <div className="border-t border-slate-100 pt-4 text-center">
          <Link to="/" className="text-xs text-slate-400 hover:text-slate-600 flex items-center justify-center gap-1">
            <ArrowLeft size={12} /> Back to Ellora EPC
          </Link>
        </div>
      </div>
    </div>
  );
}
